
import { useState, useRef, useEffect } from 'react';

interface ShareButtonsProps {
  url: string;
  title: string;
  size?: 'sm' | 'md';
  variant?: 'inline' | 'dropdown';
}

export default function ShareButtons({ url, title, size = 'sm', variant = 'dropdown' }: ShareButtonsProps) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fullUrl = url.startsWith('http') ? url : `${window.location.origin}${url}`;

  const share = (platform: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const shareUrls: Record<string, string> = {
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(fullUrl)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(fullUrl)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(fullUrl)}`,
      email: `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(`Check this out: ${fullUrl}`)}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(`${title} ${fullUrl}`)}`
    };

    if (platform === 'copy') {
      navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      return;
    }

    window.open(shareUrls[platform], '_blank', 'width=600,height=400,noopener,noreferrer');
    setOpen(false);
  };

  const iconSize = size === 'sm' ? 'w-8 h-8 text-sm' : 'w-10 h-10 text-lg';

  const buttons = (
    <>
      <button
        onClick={(e) => share('twitter', e)}
        className={`${iconSize} flex items-center justify-center rounded-full bg-slate-100 hover:bg-sky-500 hover:text-white text-slate-500 transition-all cursor-pointer`}
        aria-label="Share on X"
        title="Share on X"
      >
        <i className="ri-twitter-x-line"></i>
      </button>
      <button
        onClick={(e) => share('facebook', e)}
        className={`${iconSize} flex items-center justify-center rounded-full bg-slate-100 hover:bg-blue-600 hover:text-white text-slate-500 transition-all cursor-pointer`}
        aria-label="Share on Facebook"
        title="Share on Facebook"
      >
        <i className="ri-facebook-fill"></i>
      </button>
      <button
        onClick={(e) => share('linkedin', e)}
        className={`${iconSize} flex items-center justify-center rounded-full bg-slate-100 hover:bg-blue-700 hover:text-white text-slate-500 transition-all cursor-pointer`}
        aria-label="Share on LinkedIn"
        title="Share on LinkedIn"
      >
        <i className="ri-linkedin-fill"></i>
      </button>
      <button
        onClick={(e) => share('whatsapp', e)}
        className={`${iconSize} flex items-center justify-center rounded-full bg-slate-100 hover:bg-green-500 hover:text-white text-slate-500 transition-all cursor-pointer`}
        aria-label="Share on WhatsApp"
        title="Share on WhatsApp"
      >
        <i className="ri-whatsapp-line"></i>
      </button>
      <button
        onClick={(e) => share('email', e)}
        className={`${iconSize} flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-700 hover:text-white text-slate-500 transition-all cursor-pointer`}
        aria-label="Share via Email"
        title="Share via Email"
      >
        <i className="ri-mail-line"></i>
      </button>
      <button
        onClick={(e) => share('copy', e)}
        className={`${iconSize} flex items-center justify-center rounded-full transition-all cursor-pointer ${
          copied
            ? 'bg-green-500 text-white'
            : 'bg-slate-100 hover:bg-amber-500 hover:text-white text-slate-500'
        }`}
        aria-label="Copy link"
        title={copied ? 'Copied!' : 'Copy link'}
      >
        <i className={copied ? 'ri-check-line' : 'ri-link'}></i>
      </button>
    </>
  );

  if (variant === 'inline') {
    return <div className="flex items-center gap-2">{buttons}</div>;
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen(!open);
        }}
        className={`${iconSize} flex items-center justify-center rounded-full bg-slate-100 hover:bg-amber-500 hover:text-white text-slate-500 transition-all cursor-pointer`}
        aria-label="Share this article"
        title="Share"
      >
        <i className="ri-share-line"></i>
      </button>

      {open && (
        <div
          className="absolute bottom-full right-0 mb-2 z-50 animate-fadeIn"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="bg-white rounded-xl shadow-2xl border border-slate-200 p-3 flex items-center gap-2">
            {buttons}
          </div>
          <div className="absolute bottom-0 right-4 translate-y-1/2 rotate-45 w-3 h-3 bg-white border-r border-b border-slate-200"></div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(4px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out forwards;
        }
      `}</style>
    </div>
  );
}
